// SAB-1172 / SAB-1173: ONE definition of "active news", shared by both sports.
//
// Before this, MLB had no definition at all: PlayerNewsIcon rendered unconditionally
// at 14 sites, so a blurb from March looked identical to one from this morning and
// the dialog could open empty. NFL had no news surface. Writing a fresh rule for NFL
// and leaving MLB's absent is exactly how the two drift apart, so the rule lives here
// and both sports import it.

/** A news item is active if it landed within this many days. */
export const ACTIVE_WINDOW_DAYS = 7;

/**
 * DELIBERATE ASYMMETRY — NFL has a second clause, MLB does not.
 *
 * NFL: active = within 7 days OR from the current game week, however old.
 * Thursday-night news about a Sunday slate is current on Sunday, and a bare 7-day
 * window would start dropping it mid-week. The week is anchored to the FIRST KICKOFF
 * of the current REG week from `nfl_games` -- never `nfl_leagues.current_week`, which
 * SAB-1169 showed drifts silently (a league sat at week 3 against a week-1 slate).
 * A drifted league must not get the wrong week's news on top of wrong scoring.
 *
 * MLB: 7-day window ONLY. Baseball has no weeks, so there is no honest equivalent of
 * "the current game week" -- inventing one (a homestand? a series? seven days by
 * another name?) would be arbitrary, and an arbitrary rule is worse than a plain one.
 * Adam, 2026-09-14. This is a decision, not an oversight; do not "fix" the asymmetry
 * by bolting a fake week onto MLB.
 */
/**
 * Start of the current game week, for the "however old" clause (SAB-1191).
 *
 * THE WEEK ANCHOR IS THE KICKOFF *DAY*, NOT THE KICKOFF INSTANT.
 *
 * Both SportRadar feeds supply DATE-ONLY values -- transactions carry `effective_date`,
 * injuries carry `status_date` -- so every row lands at 00:00:00Z. Week 1's first kickoff
 * is 00:20:00Z on 2026-09-10. Comparing a midnight row against a 00:20 anchor excluded
 * all 43 transactions filed on opening day: twenty minutes too early, by construction,
 * for a feed that cannot express a time at all.
 *
 * That made a deliberately redundant rule inert exactly where the redundancy mattered.
 * Five weeks this season run LONGER than the 7-day window -- REG 1 (8.0 days), REG 12
 * (8.0), REG 17 (9.7), PST 2 (7.9), PST 3 (14.1, the pre-Super-Bowl bye) -- so on those
 * weeks the other clause cannot cover the gap, and kickoff-day news would go dark while
 * the week was still running.
 *
 * Truncating the ANCHOR rather than shifting the DATA is deliberate: `published_at` stays
 * the feed's own date and is never fabricated (SAB-1172), card ordering is untouched, and
 * no migration is needed. It also does not over-include -- an injury dated 09-08 is still
 * before a 09-10 anchor and stays correctly out.
 */
export function weekAnchorStart(kickoff: string | Date): Date | null {
  const k = kickoff instanceof Date ? kickoff : new Date(kickoff);
  if (Number.isNaN(k.getTime())) return null;
  return new Date(Date.UTC(k.getUTCFullYear(), k.getUTCMonth(), k.getUTCDate()));
}

/** The same anchor as an ISO string, for a database predicate. */
export function weekAnchorStartIso(kickoff: string | Date): string | null {
  return weekAnchorStart(kickoff)?.toISOString() ?? null;
}

export function isActiveNews(
  publishedAt: string | Date,
  opts: { now?: Date; weekFirstKickoff?: string | Date | null } = {},
): boolean {
  const now = opts.now ?? new Date();
  const published = publishedAt instanceof Date ? publishedAt : new Date(publishedAt);
  if (Number.isNaN(published.getTime())) return false;

  const windowStart = new Date(now.getTime() - ACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  if (published >= windowStart) return true;

  // NFL only: anything from the current game week, regardless of age.
  // Anchored to the kickoff DAY -- see weekAnchorStart above.
  if (opts.weekFirstKickoff) {
    const anchor = weekAnchorStart(opts.weekFirstKickoff);
    if (anchor && published >= anchor) return true;
  }
  return false;
}

/** ISO cutoff for the 7-day half, for use in a database predicate. */
export function activeWindowStartIso(now: Date = new Date()): string {
  return new Date(now.getTime() - ACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
}
